import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "@/store/hooks";
import { fetchCurrentUser } from "@/store/slices/userSlice";
import { loadUserMenus } from "@/store/slices/authSlice";
import { authApi } from "@/api/auth";

/**
 * Ticket-based login page for WebView authentication.
 *
 * URL: /auth/ticket-login?ticket=xxx&redirect=/target-page&embed=true&hideNav=true
 *
 * Flow:
 * 1. Read ticket from URL params
 * 2. Exchange ticket for JWT via backend API
 * 3. Store tokens in localStorage
 * 4. Load user info and menus
 * 5. Redirect to target page
 */
export default function TicketLoginPage() {
    const { t } = useTranslation("auth");
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [error, setError] = useState<string | null>(null);
    // Prevent React StrictMode double-invocation from exchanging ticket twice
    const exchangeAttempted = useRef(false);

    const ticket = searchParams.get("ticket");
    const redirect = searchParams.get("redirect") || "/dashboard";
    const embed = searchParams.get("embed");
    const hideNav = searchParams.get("hideNav");

    useEffect(() => {
        if (!ticket) {
            setError(t("ticketLogin.noTicket"));
            return;
        }

        // Guard: ticket is one-time-use, only attempt exchange once.
        // This ref also prevents React StrictMode double-invocation issues.
        // We intentionally do NOT use a `cancelled` cleanup flag here because
        // StrictMode's fake unmount/remount would cancel the first (and only)
        // exchange attempt before it completes.
        if (exchangeAttempted.current) return;
        exchangeAttempted.current = true;

        const doExchange = async () => {
            try {
                // Step 1: Exchange ticket for tokens
                console.log("[TicketLogin] Exchanging ticket...");
                const tokens = await authApi.exchangeTicket(ticket);
                console.log("[TicketLogin] Exchange response:", JSON.stringify(tokens)?.slice(0, 200));

                // Step 2: Store tokens
                localStorage.setItem("ACCESS_TOKEN", tokens.access_token);
                localStorage.setItem("REFRESH_TOKEN", tokens.refresh_token);

                // Step 2.5: Notify AuthContext that token is available
                // AuthContext listens for this event in WebView mode to trigger auth state re-check
                window.dispatchEvent(new CustomEvent('tokenInjected', {
                    detail: { token: tokens.access_token }
                }));

                // Step 3: Load user info and menus (best-effort, don't block redirect)
                try {
                    await Promise.all([
                        dispatch(fetchCurrentUser()).unwrap(),
                        dispatch(loadUserMenus()).unwrap(),
                    ]);
                } catch (loadErr) {
                    console.warn("[TicketLogin] Non-critical: failed to preload user data:", loadErr);
                }

                // Step 4: Build redirect URL preserving embed params
                let targetPath = redirect;
                if (embed || hideNav) {
                    const url = new URL(targetPath, window.location.origin);
                    if (embed) url.searchParams.set("embed", embed);
                    if (hideNav) url.searchParams.set("hideNav", hideNav);
                    targetPath = url.pathname + url.search;
                }

                // Step 5: Navigate to target page
                void navigate(targetPath, { replace: true });
            } catch (err: any) {
                console.error("[TicketLogin] Exchange failed:", err);
                const message =
                    err.response?.data?.msg ||
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to authenticate. The link may have expired.";
                setError(message);
            }
        };

        void doExchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ticket is one-time; t is stable from useTranslation
    }, [ticket]);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center max-w-md px-6">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-950/50 rounded-full flex items-center justify-center">
                        <svg
                            className="w-8 h-8 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold text-foreground mb-2">
                        {t("ticketLogin.failed.title")}
                    </h1>
                    <p className="text-muted-foreground">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4">
                    <div className="w-16 h-16 border-4 border-[#3A4B76]/20 border-t-[#3A4B76] rounded-full animate-spin" />
                </div>
                <p className="text-lg font-medium text-foreground">
                    {t("ticketLogin.authenticating")}
                </p>
                <p className="text-sm text-muted-foreground mt-1">{t("ticketLogin.pleaseWait")}</p>
            </div>
        </div>
    );
}
